/**
 * UserWiseCountTable.jsx — the "User-wise Count" tab.
 *
 * Straight from GET /api/ocean-query-log/user-counts, which aggregates per
 * user and per type. It's an aggregation over the whole filtered set (not the
 * table's 2000-row window), so these totals can exceed what the Log Details
 * tab lists.
 *
 * CONFIRMATION AWAITED (the create-booking call) and BOOKING (the KAM's
 * /ocean-booking/confirm) are separate columns — they're separate steps. Booking
 * updates still have no column of their own: they're folded into BOOKING (with
 * the split in the cell tooltip) so that Rate search + Confirmation awaited +
 * Booking + Cancelled always reconciles with Total. Search and sort live in
 * UserFilterBar; rows arrive already narrowed and ordered.
 */
import React from "react";
import { initialsOf } from "./logUtils";

const HEAD_CLASS = "px-4 py-3 text-[11px] font-semibold tracking-[0.06em] text-gray-500 uppercase";
const FOOT_CLASS = "px-4 py-3.5 text-[13px] font-semibold";

/** Counts read as their type's colour — the same blue / amber / green / red as the chips. */
const RATE_COLOR = "text-[#2563EB]";
const AWAITED_COLOR = "text-[#D97706]";
const BOOKING_COLOR = "text-[#16A34A]";
const CANCEL_COLOR = "text-[#EF4444]";

function UserWiseCountTable({ rows }) {
  // One pass for the footer: bookings and booking_updates are summed together
  // to match the BOOKING column above.
  const totals = rows.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      rate_search: acc.rate_search + row.rate_search,
      confirmation_awaited: acc.confirmation_awaited + row.confirmation_awaited,
      bookings: acc.bookings + row.bookings + row.booking_updates,
      booking_cancel: acc.booking_cancel + row.booking_cancel,
    }),
    { total: 0, rate_search: 0, confirmation_awaited: 0, bookings: 0, booking_cancel: 0 }
  );

  return (
    <div className="rounded-xl border border-gray-200 overflow-x-auto bg-white">
      <table className="w-full min-w-[980px] border-collapse">
        <thead>
          <tr className="bg-[#F1F3F5]">
            <th className={`${HEAD_CLASS} w-[70px] text-left`}>S.no</th>
            <th className={`${HEAD_CLASS} text-left`}>User</th>
            <th className={`${HEAD_CLASS} w-[150px] text-center`}>Rate search</th>
            <th className={`${HEAD_CLASS} w-[160px] text-center`}>Confirmation awaited</th>
            <th className={`${HEAD_CLASS} w-[140px] text-center`}>Booking</th>
            <th className={`${HEAD_CLASS} w-[140px] text-center`}>Cancelled</th>
            <th className={`${HEAD_CLASS} w-[120px] text-right`}>Total</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr key={row.key} className="border-b border-gray-100 hover:bg-[#FAFAFA]">
              <td className="px-4 py-3.5 text-[13px] text-gray-400">{index + 1}</td>
              <td className="px-4 py-3.5">
                <span className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-primary text-[12px] font-semibold text-white">
                    {initialsOf(row.requested_by)}
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-[13px] font-medium text-gray-700">{row.requested_by}</span>
                    {row.email && row.email !== row.requested_by && (
                      <span className="text-[11px] text-gray-400">{row.email}</span>
                    )}
                  </span>
                </span>
              </td>
              <td className={`px-4 py-3.5 text-[13px] text-center ${RATE_COLOR}`}>{row.rate_search}</td>
              <td className={`px-4 py-3.5 text-[13px] text-center ${AWAITED_COLOR}`}>{row.confirmation_awaited}</td>
              <td
                className={`px-4 py-3.5 text-[13px] text-center ${BOOKING_COLOR}`}
                title={`${row.bookings} new · ${row.booking_updates} update${row.booking_updates === 1 ? "" : "s"}`}
              >
                {row.bookings + row.booking_updates}
              </td>
              <td className={`px-4 py-3.5 text-[13px] text-center ${CANCEL_COLOR}`}>{row.booking_cancel}</td>
              <td className="px-4 py-3.5 text-[13px] font-semibold text-right text-gray-900">{row.total}</td>
            </tr>
          ))}

          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-14 text-center text-[13px] text-gray-400">
                No ocean API activity for the current filters.
              </td>
            </tr>
          )}
        </tbody>

        {rows.length > 0 && (
          <tfoot>
            <tr className="bg-[#FFF8E7] border-t border-[#F6E3B4]">
              <td className={`${FOOT_CLASS} text-left text-gray-800`} colSpan={2}>
                Total ({rows.length} {rows.length === 1 ? "user" : "users"})
              </td>
              <td className={`${FOOT_CLASS} text-center ${RATE_COLOR}`}>{totals.rate_search}</td>
              <td className={`${FOOT_CLASS} text-center ${AWAITED_COLOR}`}>{totals.confirmation_awaited}</td>
              <td className={`${FOOT_CLASS} text-center ${BOOKING_COLOR}`}>{totals.bookings}</td>
              <td className={`${FOOT_CLASS} text-center ${CANCEL_COLOR}`}>{totals.booking_cancel}</td>
              <td className={`${FOOT_CLASS} text-right text-gray-900`}>{totals.total}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default UserWiseCountTable;
